/**
 * Hooks React Query pour récupérer les membres d'un établissement
 * Utilise l'endpoint d'agrégation Gateway
 */

import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  directoryClient,
  type EstablishmentMemberWithUser,
  type ClassMemberWithUser,
  type GroupMemberWithUser,
} from '@sphere/gateway/shared';

/** Options communes pour les hooks de membres */
type MemberQueryOptions = Omit<
  UseQueryOptions<EstablishmentMemberWithUser[], Error>,
  'queryKey' | 'queryFn'
>;

/** Configuration de cache par défaut */
const DEFAULT_CACHE_CONFIG = {
  staleTime: 2 * 60 * 1000, // 2 minutes
  gcTime: 5 * 60 * 1000, // 5 minutes
} as const;

/**
 * Factory pour créer des hooks de membres établissement
 * Évite la duplication de code entre les différents hooks
 */
function createEstablishmentMembersHook(
  queryKeySuffix: string,
  fetchFn: (establishmentId: string) => Promise<EstablishmentMemberWithUser[]>
) {
  return function useHook(
    establishmentId: string | undefined,
    options?: MemberQueryOptions
  ) {
    return useQuery({
      queryKey: [
        'directory',
        'establishments',
        establishmentId,
        queryKeySuffix,
      ],
      queryFn: () => {
        if (!establishmentId) {
          throw new Error('establishmentId is required');
        }
        return fetchFn(establishmentId);
      },
      enabled: !!establishmentId,
      ...DEFAULT_CACHE_CONFIG,
      ...options,
    });
  };
}

/**
 * Hook pour récupérer tous les membres d'un établissement
 */
export const useEstablishmentMembers = createEstablishmentMembersHook(
  'members',
  directoryClient.getEstablishmentMembers.bind(directoryClient)
);

export type AssignableStudentFilters = { classId?: string | null };
export type AssignableTeacherFilters = {
  classId?: string | null;
  /** Restreindre aux enseignants ayant déjà un ClassMembership enseignant dans la classe */
  classTeachersOnly?: boolean;
};

/**
 * Élèves assignables (rôle STUDENT). Si `classId`, exclut les élèves déjà dans la classe.
 */
export function useAssignableStudents(
  establishmentId: string | undefined,
  filters?: AssignableStudentFilters,
  options?: MemberQueryOptions
) {
  const classId = filters?.classId ?? undefined;
  return useQuery({
    queryKey: [
      'directory',
      'establishments',
      establishmentId,
      'assignable-students',
      classId ?? '',
    ],
    queryFn: () => {
      if (!establishmentId) {
        throw new Error('establishmentId is required');
      }
      return directoryClient.getAssignableStudents(establishmentId, {
        classId,
      });
    },
    enabled: !!establishmentId,
    ...DEFAULT_CACHE_CONFIG,
    ...options,
  });
}

/**
 * Enseignants (TEACHER). Filtres optionnels alignés sur ClassMembership via l’API directory.
 */
export function useAssignableTeachers(
  establishmentId: string | undefined,
  filters?: AssignableTeacherFilters,
  options?: MemberQueryOptions
) {
  const classId = filters?.classId ?? undefined;
  const mode = filters?.classTeachersOnly ? 'class-staff' : 'enroll';
  return useQuery({
    queryKey: [
      'directory',
      'establishments',
      establishmentId,
      'assignable-teachers',
      classId ?? '',
      mode,
    ],
    queryFn: () => {
      if (!establishmentId) {
        throw new Error('establishmentId is required');
      }
      return directoryClient.getAssignableTeachers(establishmentId, {
        classId,
        classTeachersOnly: filters?.classTeachersOnly,
      });
    },
    enabled: !!establishmentId,
    ...DEFAULT_CACHE_CONFIG,
    ...options,
  });
}

/**
 * Hook pour récupérer les membres d'une classe avec leurs infos utilisateur
 * Utilise l'endpoint d'agrégation Gateway
 */
export function useClassMembers(
  classId: string | undefined,
  options?: Omit<
    UseQueryOptions<ClassMemberWithUser[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ['directory', 'classes', classId, 'members'],
    queryFn: () => {
      if (!classId) {
        return [];
      }
      return directoryClient.getClassMembers(classId);
    },
    enabled: !!classId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}

/**
 * Hook pour récupérer les membres d'un groupe avec leurs infos utilisateur
 * Utilise l'endpoint d'agrégation Gateway
 */
export function useGroupMembers(
  groupId: string | undefined,
  options?: Omit<
    UseQueryOptions<GroupMemberWithUser[], Error>,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery({
    queryKey: ['directory', 'groups', groupId, 'members'],
    queryFn: () => {
      if (!groupId) {
        throw new Error('groupId is required');
      }
      return directoryClient.getGroupMembers(groupId);
    },
    enabled: !!groupId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}
